import { ChangeEvent, useCallback, useRef, useState } from "react"
import { useField } from "formik"

import { SWAP_SLIPPAGE_DEFAULT_VALUE } from "#/shared/constants"
import { SwapSlippageError } from "#/shared/types"

const useSwapSlippageInput = () => {
  const [error, setError] = useState("")

  const [, { value: slippage }, helpersSlippage] = useField("slippage")

  const lastSlippageRef = useRef(slippage)

  const inputChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target
      const normalizedValue = value.trim()
      const isValidNumber = !Number.isNaN(+normalizedValue)
      const leadingZeroPattern = /^0\d+/

      if (normalizedValue === "") {
        setError("")
        helpersSlippage.setValue("")
        return
      }

      // Allow for a precision of 0.01
      const decimalsPart = normalizedValue.split(".")[1]
      const isSmallerThanPrecision = !!decimalsPart && decimalsPart.length > 2

      if (
        !isValidNumber ||
        leadingZeroPattern.test(normalizedValue) ||
        isSmallerThanPrecision
      )
        return

      const parsedNormalizedValue = +normalizedValue

      if (parsedNormalizedValue >= 100 || parsedNormalizedValue === 0) {
        setError(SwapSlippageError.INVALID)
      } else if (parsedNormalizedValue < SWAP_SLIPPAGE_DEFAULT_VALUE) {
        setError(SwapSlippageError.TOO_SMALL)
      } else {
        setError("")
      }

      if (+normalizedValue > 100) return

      helpersSlippage.setValue(normalizedValue)
    },
    [helpersSlippage],
  )

  const onConfirmSlippage = useCallback(() => {
    lastSlippageRef.current = slippage
  }, [lastSlippageRef, slippage])

  const resetSlippageInput = useCallback(() => {
    setError("")
    helpersSlippage.setValue(lastSlippageRef.current)
  }, [setError, helpersSlippage, lastSlippageRef])

  return {
    onConfirmSlippage,
    onInputChange: inputChangeHandler,
    error,
    clearError: () => setError(""),
    resetSlippageInput,
  }
}

export default useSwapSlippageInput
